import {
  Button,
  ButtonType,
  ConfirmationModal,
  FlexProps,
  ModalType,
} from '@access-erp/ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import {
  END_DATE_BEFORE_START_DATE,
  START_DATE_AFTER_END_DATE,
} from '@/config/text.const';
import useEditPurchaseOrder from '../../hooks/useEditPurchaseOrder';
import useGetPurchaseOrders from '@/pages/ContractDetails/ContractLines/hooks/useGetPurchaseOrders';
import { CurrencyOption }
  // eslint-disable-next-line max-len
  from '../../../ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AddPurchaseOrderModal/validationSchema';

import styles from './edit-purchase-order.module.scss';
import SetPOCancelledConfirm from './SetPOCancelledConfirm';
import { StatusOption, validationSchema } from './validationSchema';
import { PurchaseOrderWithId } from '@/utils/data-helpers/types';
import { CurrencyType, PurchaseOrderOutputType, PurchaseOrderStatusEnum }
  from '@/graphql/contracts/_generated_/contracts';
import { filterPurchaseOrdersStatuses } from '@/config/statuses.config';
import { useGetGridContractLines } from '@/pages/ContractLines/hooks/useGetGridContractLines';
import { NumberUtils } from '@/utils/numbers.utils';
import { isValidDate } from '@/utils/date.utils';

interface EditPurchaseOrderProps {
  onClose: () => void;
  isVisible: boolean;
  refreshGrid: () => void;
  purchaseOrder: PurchaseOrderWithId;
  currencies: CurrencyType[];
}

export function EditPurchaseOrderModal({
  onClose,
  isVisible,
  refreshGrid,
  purchaseOrder,
  currencies,
}: EditPurchaseOrderProps) {
  const statusOptions = filterPurchaseOrdersStatuses;
  const currenciesOptions = useMemo(() => {
    return currencies.map((currency) => ({
      label: currency.formattedCurrency,
      value: currency.currency,
    })) as CurrencyOption[];
  }, [currencies]);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      purchaseOrderId: purchaseOrder.id,
      customerId: purchaseOrder.customerId,
      poDate: purchaseOrder.poDate,
      poStartDate: purchaseOrder.poStartDate,
      poEndDate: purchaseOrder.poEndDate,
      poTotalValue: parseFloat(purchaseOrder.poTotalValue ?? ''),
      poRemainingValue: purchaseOrder.poRemainingValue,
      poStatus: statusOptions.filter((x) => x.value === purchaseOrder.poStatus)[0]?.value,
      poCurrency: purchaseOrder.poCurrency,
    },
    values: {
      poNumber: purchaseOrder.poNumber,
      poNote: purchaseOrder.poNote,
    },
  });

  const { editPurchaseOrder } = useEditPurchaseOrder();

  const { getPurchaseOrdersItems } = useGetPurchaseOrders();
  const [customerPos, setCustomerPos] = useState<PurchaseOrderOutputType[] | undefined | null>([]);

  const [isVisibleCancelModal, setShowConfirmationModal] = useState<
  'status' | 'currency' | 'both' | null
  >(null);

  useEffect(() => {
    const fetchCustomerPos = async () => {
      const res = await getPurchaseOrdersItems({
        filter: {
          customerCode: { eq: purchaseOrder.customerCode },
        },
        first: 9999999,
      });
      setCustomerPos(res as any);
    };
    fetchCustomerPos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrder.customerCode]);

  const { getGridContractLines } = useGetGridContractLines();
  const [lineCount, setLineCount] = useState(0);

  useEffect(() => {
    const fetchLineCount = async () => {
      const contractLines = await getGridContractLines({
        filter: {
          poNumber: { eq: purchaseOrder.poNumber },
        },
      });
      setLineCount(contractLines?.items?.length ?? 0);
    };

    fetchLineCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrder.poNumber]);

  const handleClose = () => {
    onClose();
    methods.reset();
  };

  const handleGridRefresh = () => {
    onClose();
    methods.reset();
    refreshGrid();
  };

  const handleEdit = () => {
    const values = methods.getValues();
    const newStatus =
      values.poStatus === '' ? undefined : (values.poStatus as PurchaseOrderStatusEnum);
    const newCurrency = values.poCurrency === purchaseOrder.poCurrency ? null : values.poCurrency;

    editPurchaseOrder(
      {
        input: {
          purchaseOrderId: purchaseOrder.id,
          customerId: (values.customerId ?? purchaseOrder.customerId) || '',
          poNumber: (values.poNumber ?? purchaseOrder.poNumber) || '',
          poDate: values.poDate ?? purchaseOrder.poDate,
          poStartDate: values.poStartDate ?? purchaseOrder.poStartDate,
          poEndDate: values.poEndDate ?? purchaseOrder.poEndDate,
          poTotalValue: values.poTotalValue,
          poStatus: newStatus ?? purchaseOrder.poStatus,
          poNote: values.poNote,
          poCurrency: newCurrency,
        },
      },
      handleGridRefresh,
    );
  };

  const handleSave = () => {
    const values = methods.getValues();
    const newStatus = values.poStatus === PurchaseOrderStatusEnum.Cancelled ? true : false;
    const newCurrency =
      values.poCurrency !== undefined &&
        values.poCurrency !== purchaseOrder.poCurrency
        ? true
        : false;

    if (newStatus && newCurrency) {
      setShowConfirmationModal('both');
    } else if (newStatus) {
      setShowConfirmationModal('status');
    } else if (newCurrency) {
      setShowConfirmationModal('currency');
    } else {
      handleEdit();
    }
  };

  const toDateValue = (dateValue: Date | string | null | undefined): Date | undefined => {
    if (!dateValue) return undefined;
    const date = new Date(dateValue);
    return isValidDate(date) ? date : undefined;
  };

  return (
    <>
      <AgModalWindow
        isOpen={isVisible}
        onClose={handleClose}
        title={`Edit ${purchaseOrder.poNumber}`}
        contentStyle={{ minWidth: '500px', maxWidth: '600px', width: '560px' }}
      >
        <FormProvider {...methods}>
          <div className={styles['dropdown-margin']}>
            <ModalFormTextInput
              id="customerId"
              name='customerId'
              labelTitle="Customer"
              requiredIcon={true}
              value={`[${purchaseOrder.customerCode}] - ${purchaseOrder.customerName}`}
              disabled={true}
            />

            <div className={styles['text-wrapper']}>
              <ModalFormTextInput
                id="poNumber"
                labelTitle="Number"
                requiredIcon={true}
                defaultValue={purchaseOrder.poNumber ?? ''}
                contentEditable={true}
                onChange={(e) => {
                  const value = e.target?.value?.trim()
                    ? e.target.value?.trim()
                    : undefined;
                  methods.setValue('poNumber', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onBlur={() => {
                  if (
                    customerPos?.some(
                      (x) =>
                        x.poNumber === methods.getValues().poNumber && x.id !== purchaseOrder.id,
                    )
                  ) {
                    methods.setError('poNumber', {
                      type: 'manual',
                      message: 'PO Number already exists for this customer',
                    });
                  }
                }}
                validationMessage={methods.formState.errors.poNumber?.message?.toString()}
              />

              <ModalFormDropdown<CurrencyOption>
                id="poCurrency"
                labelTitle="Currency"
                requiredIcon={true}
                optionsData={currenciesOptions}
                getOptionLabel={(option) => option?.label || ''}
                getOptionValue={(option) => option?.value || ''}
                selectedOption={
                  currenciesOptions.find(
                    c => c.value === (methods.getValues().poCurrency || purchaseOrder.poCurrency)
                  ) || null
                }
                handleChange={(selectedOption: CurrencyOption | null) => {
                  methods.setValue('poCurrency', selectedOption?.value || '', {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                  });
                }}
                isSearchable={true}
                defaultValue={currenciesOptions.find(c => c.value === purchaseOrder.poCurrency) || null}
                validationMessage={methods.formState.errors.poCurrency?.message?.toString()}

              />
            </div>

            <div className={styles['text-wrapper']}>
              <ModalFormTextInput
                id="poTotalValue"
                labelTitle="Total Value"
                defaultValue={NumberUtils.prettifyWithRound(purchaseOrder.poTotalValue) ?? ''}
                onChange={(e) => {
                  const value = e.target.value?.trim();
                  let decimalValue: number | undefined = undefined;
                  if (value === '' || value === undefined) {
                    decimalValue = undefined;
                  } else {
                    const parsed = NumberUtils.parseNumericInput(value);
                    decimalValue = isNaN(parsed) ? undefined : parsed;
                  }

                  methods.setValue('poTotalValue', decimalValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                style={{ textAlign: 'right' }}
                validationMessage={methods.formState.errors.poTotalValue?.message?.toString()}
              />

              <ModalFormTextInput
                id="poRemainingValue"
                labelTitle="Remaining Value"
                value={NumberUtils.prettifyWithRound(purchaseOrder.poRemainingValue) ?? ''}
                disabled={true}
                style={{ textAlign: 'right' }}
              />
            </div>

            <div className={styles['text-wrapper']}>
              <div>
                <label htmlFor="poDate">Date</label>
                <DatePickerTag4
                  id="poDate"
                  value={toDateValue(methods.getValues().poDate ?? purchaseOrder.poDate)}
                  onChange={({ value }) => {
                    methods.setValue('poDate', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
              </div>

              <div>
                <label htmlFor="poStartDate">Start Date</label>
                <DatePickerTag4
                  id="poStartDate"
                  value={toDateValue(methods.getValues().poStartDate ?? purchaseOrder.poStartDate)}
                  onChange={({ value }) => {
                    methods.setValue('poStartDate', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    methods.trigger('poEndDate');

                    const endDate = methods.getValues().poEndDate ?? purchaseOrder.poEndDate;
                    if (value && dayjs(value).isAfter(dayjs(endDate), 'day')) {
                      methods.setError('poStartDate', {
                        type: 'manual',
                        message: START_DATE_AFTER_END_DATE,
                      });
                    } else {
                      methods.clearErrors('poEndDate');
                    }
                  }}
                />
                {methods.formState.errors.poStartDate && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {methods.formState.errors.poStartDate.message as string}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="poEndDate">End Date</label>
                <DatePickerTag4
                  id="poEndDate"
                  value={toDateValue(methods.getValues().poEndDate ?? purchaseOrder.poEndDate)}
                  onChange={({ value }) => {
                    methods.setValue('poEndDate', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    methods.trigger('poEndDate');

                    const startDate = methods.getValues().poStartDate ?? purchaseOrder.poStartDate;
                    if (value && dayjs(startDate).isAfter(dayjs(value), 'day')) {
                      methods.setError('poEndDate', {
                        type: 'manual',
                        message: END_DATE_BEFORE_START_DATE,
                      });
                    } else {
                      methods.clearErrors('poStartDate');
                    }
                  }}
                />
                {methods.formState.errors.poEndDate && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {methods.formState.errors.poEndDate.message as string}
                  </div>
                )}
              </div>
            </div>

            <div className={styles['text-wrapper']}>
              <ModalFormDropdown<StatusOption>
                id="poStatus"
                labelTitle="Status"
                requiredIcon={true}
                optionsData={statusOptions}
                getOptionLabel={(option) => option?.label || ''}
                getOptionValue={(option) => option?.value || ''}
                handleChange={(selectedOption: StatusOption | null) => {
                  methods.setValue('poStatus', selectedOption?.value || '', {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                  });
                }}
                isSearchable={true}
                defaultValue={statusOptions.find(s => s.value === purchaseOrder.poStatus) || null}
                validationMessage={methods.formState.errors.poStatus?.message?.toString()}
              />
              <div></div>
            </div>

            <div>
              <ModalFormTextarea
                id="poNote"
                labelTitle="Note"
                // value={purchaseOrder.poNote ?? ''}
                defaultValue={purchaseOrder.poNote ?? ''}
                onChange={(e) => {
                  const value = e.target?.value?.trim()
                    ? e.target.value.trim()
                    : undefined;
                  methods.setValue('poNote', value, { shouldValidate: true, shouldDirty: true });
                }}
                validationMessage={methods.formState.errors.poNote?.message?.toString()}
              />
            </div>
          </div>
        </FormProvider>

        <div className={styles['modal-buttons']}>
          <Button
            wrapperStyle={{ width: 'initial', flex: 'none' }}
            buttonType={ButtonType.Secondary}
            text="Cancel"
            onClick={handleClose}
          />
          <Button
            buttonType={ButtonType.Primary}
            disabled={!methods.formState.isDirty || !methods.formState.isValid}
            wrapperStyle={{ width: 'initial', flex: 'none' }}
            text="Save"
            onClick={() => handleSave()}
          ></Button>
        </div>
      </AgModalWindow>

      <ConfirmationModal
        open={!!isVisibleCancelModal}
        onClose={() => setShowConfirmationModal(null)}
        onConfirm={() => handleEdit()}
        confirmButtonText="Confirm"
        title="Please confirm you wish to continue"
        type={ModalType.Warning}
        footerAlign={FlexProps['space-between']}
      >
        <SetPOCancelledConfirm lineCount={lineCount} editMode={isVisibleCancelModal} />
      </ConfirmationModal>
    </>
  );
}
